import { State } from '@state/model';
import { Injectable } from '@angular/core';
import { ofType } from '@ces/sourced-action';
import { Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { selectPPTInstallmentMonths } from '@/app/modules/ppt/state/ppt-state/ppt-state.selectors';
import { selectSignedIn } from '@session/state/session.selectors';
import { of } from 'rxjs';
import { catchError, delay, filter, first, map, mapTo, mergeMap, mergeMapTo, switchMap, withLatestFrom } from 'rxjs/operators';
import { autoDraftDisabled, autoDraftDisableFailed, autoDraftSaved, autoDraftSaveFailed, disableAutoDraft, flushAutoDraftState, loadAutoDrafts, removeAutoDraft, saveInstallmentsAutoDraft, saveRegularAutoDraft, setAutoDraft, setAutoDrafts } from './auto-draft.actions';
import { selectAutoDrafts } from './auto-draft.selectors';
import { signedOut } from '../../session/state/session.actions';
import { createEgovAPIError } from 'egov-api';


@Injectable()
export class AutoDraftEntityEffects
{
    constructor
    (
        // Dependencies

        private readonly actions$: Actions,
        private readonly store: Store<State>,
    )
    {}


    // Load

    private readonly loadAutoDrafts$ = createEffect(() => this.actions$.pipe(
        ofType(loadAutoDrafts),
        switchMap(() => this.store.select(selectSignedIn).pipe(
            first(),
            filter(signedIn => signedIn),
        )),
        switchMap(_ => this.store.select(selectAutoDrafts).pipe(first())),
        delay(2000),
        map(autoDrafts => setAutoDrafts(
            `Effect of ${loadAutoDrafts.type}`,
            { autoDraftsData: autoDrafts },
        )),
    ));


    // Save

    private readonly saveRegularAutoDraft$ = createEffect(() => this.actions$.pipe(
        ofType(saveRegularAutoDraft),
        mergeMap(({ operationId, accountNumber, paymentMethodId, month, day, notifications }) =>
        {
            const now = new Date();
            const passedThisYear = month < now.getMonth() || month === now.getMonth() && day <= now.getDate();

            return of({
                accountNumber,
                paymentMethodId,
                scheduling: { month, day },
                nextDraft:
                    new Date(
                        passedThisYear
                            ? now.getFullYear() + 1
                            : now.getFullYear(),
                        month,
                        day
                    ).getTime(),
                notifications,
            }).pipe(
                delay(2000),
                map(_ => { if (Math.random() < .95) return _; else throw createEgovAPIError('Simulated error'); }),
                mergeMap(autoDraftData => of(
                    setAutoDraft(`Effect of ${saveRegularAutoDraft.type}`, { autoDraftData }),
                    autoDraftSaved(`Effect of ${saveRegularAutoDraft.type}`, { operationId }),
                )),
                catchError(error => of(autoDraftSaveFailed(
                    `Effect of ${saveRegularAutoDraft.type}`,
                    { operationId }
                ))),
            );
        }),
    ));

    private readonly saveInstallmentsAutoDraft$ = createEffect(() => this.actions$.pipe(
        ofType(saveInstallmentsAutoDraft),
        withLatestFrom(this.store.select(selectPPTInstallmentMonths).pipe(
            filter(ms => !!ms),
            map(ms => ms![0])
        )),
        mergeMap(([{ operationId, accountNumber, paymentMethodId, days, notifications }, month]) =>
        {
            const day: number = days[0];

            const now = new Date();
            const passedThisYear: boolean =
                month < now.getMonth() || month === now.getMonth() && day <= now.getDate();

            return of({
                accountNumber,
                paymentMethodId,
                scheduling: { days },
                nextDraft:
                    new Date(
                        passedThisYear
                            ? now.getFullYear() + 1
                            : now.getFullYear(),
                        month,
                        day
                    ).getTime(),
                notifications,
            }).pipe(
                delay(2000),
                map(_ => { if (Math.random() < .95) return _; else throw createEgovAPIError('Simulated error'); }), // Fail 5% of the times
                mergeMap(autoDraftData => of(
                    setAutoDraft(`Effect of ${saveInstallmentsAutoDraft.type}`, { autoDraftData }),
                    autoDraftSaved(`Effect of ${saveInstallmentsAutoDraft.type}`, { operationId }),
                )),
                catchError(error => of(
                    autoDraftSaveFailed(`Effect of ${saveInstallmentsAutoDraft.type}`, { operationId }),
                )),
            );
        }),
    ));

    private readonly disableAutoDraft$ = createEffect(() => this.actions$.pipe(
        ofType(disableAutoDraft),
        mergeMap(({ accountNumber }) =>
            of(removeAutoDraft(`Effect of ${disableAutoDraft.type}`, { accountNumber })).pipe(
                delay(1000),
                map(_ => { if (Math.random() < .95) return _; else throw createEgovAPIError('Simulated error'); }), // Fail 5% of the times
                mergeMapTo(of(
                    removeAutoDraft(`Effect of ${disableAutoDraft.type}`, { accountNumber }),
                    autoDraftDisabled(`Effect of ${disableAutoDraft.type}`, { accountNumber }),
                )),
                catchError(error => of(
                    autoDraftDisableFailed(`Effect of ${disableAutoDraft.type}`, { accountNumber }),
                )),
            )
        ),
    ));

    private readonly clearAutoDraftsOnSignedOut$ = createEffect(() => this.actions$.pipe(
        ofType(signedOut),
        mapTo(flushAutoDraftState(`Effect of ${signedOut.type}`)),
    ));
}
